import React, { useState, useEffect, useRef } from 'react';
import styles from './index.module.scss';
import Toast from '@components/Toast';
import { setPostImage, commonImage } from '@images/imageUrls';
import { BASE_IMG_URL } from '@images/imageUrls';

export interface Topic {
    id: string;
    name: string;
    cover: string;
    count: string;
    category?: string;
    // 统计字段（可选）
    threadCount?: number;
    thread_count?: number;
    likeCount?: number;
    like_count?: number;
    postCount?: number;
    post_count?: number;
    shareCount?: number;
    share_count?: number;
}

interface TopicPickerProps {
    visible: boolean;
    onClose: () => void;
    onSelect: (topics: Topic[]) => void;
    topicList: Topic[];
    categoryList?: string[];
    onCategoryChange?: (categoryName: string, page?: number) => void;
    initialSelected?: Topic[];
    /** 用于请求失败后重新挂载时恢复分类。 */
    initialCategory?: string;
    /** 同步勾选草稿，不触发确认。 */
    onSelectionChange?: (topics: Topic[]) => void;
    position?: {
        top?: number;
        left?: number;
        right?: number;
        bottom?: number;
    };
    topicStore?: any; // 添加topicStore作为备用搜索方案
    hasMore?: boolean; // 是否还有更多数据（后端分页）
    isLoadingMore?: boolean; // 是否正在加载更多
    /** 默认沿用发帖页的点击外部确认行为，转换流程可选择仅关闭。 */
    outsideClickBehavior?: 'confirm' | 'close';
    closeOnSelect?: boolean;
    /** 显式加载状态，用于区分已加载的空分类。 */
    loading?: boolean;
}

const TopicPicker: React.FC<TopicPickerProps> = ({ visible, onClose, onSelect, topicList, categoryList = [], onCategoryChange, initialSelected = [], initialCategory, onSelectionChange, position = {}, topicStore, hasMore: hasMoreProp = true, isLoadingMore: isLoadingMoreProp = false, outsideClickBehavior = 'confirm', closeOnSelect = true, loading }) => {
    const [activeCategory, setActiveCategory] = useState(initialCategory || categoryList[0] || '');
    const [searchText, setSearchText] = useState('');
    const [selectedTopics, setSelectedTopics] = useState<Topic[]>([]);
    const [cachedTopicList, setCachedTopicList] = useState<Topic[]>(topicList.length > 0 ? topicList : []);
    const [searchResults, setSearchResults] = useState<Topic[]>([]);
    const [isSearching, setIsSearching] = useState(false);
    const [hasSearched, setHasSearched] = useState(false);
    const pickerRef = useRef<HTMLDivElement>(null);
    const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);
    const searchRequestRef = useRef(0);
    const listRef = useRef<HTMLDivElement>(null);
    // 分页相关状态
    const [page, setPage] = useState(1);
    const [isLoadingMore, setIsLoadingMore] = useState(isLoadingMoreProp);
    const [hasMore, setHasMore] = useState(hasMoreProp);

    useEffect(() => {
        if (initialSelected && initialSelected.length > 0) {
            const preset: Topic[] = initialSelected.slice(0, 3).map(topic => ({
                ...topic,
            }));
            setSelectedTopics(preset);
        } else if (initialSelected && initialSelected.length === 0) {
            setSelectedTopics([]);
        }
    }, [initialSelected]);

    useEffect(() => {
        if (!visible) {
            setSearchText('');
            return;
        }

        const handleClickOutside = (e: MouseEvent) => {
            if (pickerRef.current && !pickerRef.current.contains(e.target as Node)) {
                // 点击弹窗以外区域时，视为确认当前已勾选的话题（与点击"确定"按钮行为一致），而不是直接丢弃选择
                if (outsideClickBehavior === 'confirm') {
                    onSelect(selectedTopics);
                }
                onClose();
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [visible, onClose, onSelect, selectedTopics, outsideClickBehavior]);

    // 只在初始化时设置默认分类，不在分类列表变化时重置
    useEffect(() => {
        if (categoryList && categoryList.length > 0 && !activeCategory) {
            setActiveCategory(categoryList[0]);
        }
    }, [categoryList]);

    useEffect(() => {
        if (visible && topicList.length === 0) {
        }
    }, [visible, topicList]);

    // 防抖搜索函数
    const debouncedSearch = useRef((searchKeyword: string) => {
        const requestId = ++searchRequestRef.current;
        if (searchTimeoutRef.current) {
            clearTimeout(searchTimeoutRef.current);
        }
        
        searchTimeoutRef.current = setTimeout(async () => {
            if (!searchKeyword.trim()) {
                setSearchResults([]);
                setHasSearched(false);
                setIsSearching(false);
                return;
            }
            
            setIsSearching(true);
            try {
                let response;
                
                // 方案一：尝试通过props传入的topicStore搜索
                if (topicStore && topicStore.getTopicList) {
                    response = await topicStore.getTopicList({
                        'filter[name]': searchKeyword.trim(),
                        'page[number]': 1,
                        'page[size]': 50
                    });
                } else {
                    // 方案二：动态导入topic相关API
                    const { default: topicAction } = await import('@stores/topic/action');
                    const topicInstance = topicAction;
                    
                    // 调用搜索API
                    response = await topicInstance.getTopicList({
                        'filter[name]': searchKeyword.trim(),
                        'page[number]': 1,
                        'page[size]': 50
                    });
                }
                
                if (requestId !== searchRequestRef.current) return;
                if (response && (response.code === 0 || response.Code === 0)) {
                    const topics = response.data?.data || response.Data?.data || [];
                    const formattedTopics = topics.map((topic: any, idx: number) => {
                        if (idx === 0) {
                        }
                        
                        // 尝试多种字段名获取统计数据
                        const threadCount = topic.threadCount || topic.thread_count || topic.postCount || topic.post_count || parseInt(topic.count) || 0;
                        const likeCount = topic.likeCount || topic.like_count || 0;
                        const postCount = topic.postCount || topic.post_count || 0;
                        const shareCount = topic.shareCount || topic.share_count || 0;
                        
                        return {
                            id: String(topic.id),
                            name: topic.name,
                            cover: topic.avatar || commonImage.official2,
                            count: `${threadCount}帖子 ${likeCount + postCount + shareCount}讨论量`,
                            category: topic.category?.name,
                            isHot: topic.isHot,
                            isNew: topic.isNew,
                            isRecommended: topic.isRecommended,
                        };
                    });
                    setSearchResults(formattedTopics);
                    setHasSearched(true);
                } else {
                    setSearchResults([]);
                    setHasSearched(true);
                }
            } catch (error) {
                if (requestId !== searchRequestRef.current) return;
                setSearchResults([]);
                setHasSearched(true);
            } finally {
                if (requestId === searchRequestRef.current) setIsSearching(false);
            }
        }, 300); // 300ms防抖
    }).current;
    
    // 搜索文本变化处理
    const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        setSearchText(value);
        debouncedSearch(value);
    };
    
    // 清理定时器
    useEffect(() => {
        return () => {
            searchRequestRef.current += 1;
            if (searchTimeoutRef.current) {
                clearTimeout(searchTimeoutRef.current);
            }
        };
    }, []);

    useEffect(() => {
        if (topicList && topicList.length > 0) {
            setCachedTopicList(topicList);
            // topicList 更新后结束加载状态
            setIsLoadingMore(false);
        }
        // 当 topicList 为空时，也要清空缓存，避免显示旧分类数据
        if (topicList && topicList.length === 0) {
            setCachedTopicList([]);
        }
    }, [topicList]);
    
    // 同步外部传入的 hasMore 和 isLoadingMore 状态
    useEffect(() => {
        setHasMore(hasMoreProp);
    }, [hasMoreProp]);

    useEffect(() => {
        setIsLoadingMore(isLoadingMoreProp);
    }, [isLoadingMoreProp]);

    // 分类变化时重置分页
    useEffect(() => {
        if (searchText) return;
        setPage(1);
        setHasMore(hasMoreProp);
    }, [activeCategory]);
    
    // 重置搜索状态当弹窗关闭时
    useEffect(() => {
        if (!visible) {
            searchRequestRef.current += 1;
            setSearchText('');
            setSearchResults([]);
            setHasSearched(false);
            setIsSearching(false);
            setPage(1);
            if (searchTimeoutRef.current) {
                clearTimeout(searchTimeoutRef.current);
            }
        }
    }, [visible]);

    // 后端分页：加载更多时调用 onCategoryChange 请求下一页
    const loadMore = () => {
        if (isLoadingMore || !hasMore || searchText) return;
        const nextPage = page + 1;
        setPage(nextPage);
        setIsLoadingMore(true);
        if (onCategoryChange) {
            console.log('[TopicPicker PC] loadMore - calling API for page:', nextPage);
            onCategoryChange(activeCategory, nextPage);
        }
    };

    // 滚动监听：触底时加载下一页
    const handleScroll = () => {
        if (searchText) return;
        if (isLoadingMore || !hasMore) return;
        const el = listRef.current;
        if (!el) return;
        const threshold = 80;
        if (el.scrollHeight - el.scrollTop - el.clientHeight <= threshold) {
            loadMore();
        }
    };

    // 绑定滚动监听
    useEffect(() => {
        const el = listRef.current;
        if (!el) return;
        el.addEventListener('scroll', handleScroll, { passive: true });
        return () => el.removeEventListener('scroll', handleScroll);
    }, [searchText, isLoadingMore, hasMore, activeCategory]);

    if (!visible) return null;

    // 直接使用 topicList，不再使用 cachedTopicList 回退
    const displayTopicList = topicList;
    const isLoading = displayTopicList.length === 0 && categoryList.length > 0;
    
    const forceLoading = loading === undefined
        ? displayTopicList.length === 0 && categoryList.length > 0
        : loading;

    // 根据搜索状态决定显示的话题列表
    let filteredTopics: Topic[] = [];
    
    if (searchText) {
        // 搜索模式：显示搜索结果
        filteredTopics = searchResults;
    } else {
        // 正常模式：只展示当前选中分类的话题，避免切换分类时残留上一分类的数据
        const currentCategoryTopics = displayTopicList.filter(topic => {
            const topicCategory = (topic as any).category || '默认';
            return topicCategory === activeCategory;
        });

        // 对话题数据进行格式化处理
        filteredTopics = currentCategoryTopics.map(topic => {
            const threadCount = topic.threadCount || topic.thread_count || topic.postCount || topic.post_count || parseInt(String(topic.count).replace(/[^0-9]/g, '')) || 0;
            const likeCount = topic.likeCount || topic.like_count || 0;
            const postCount = topic.postCount || topic.post_count || 0;
            const shareCount = topic.shareCount || topic.share_count || 0;
            
            return {
                ...topic,
                count: topic.count && typeof topic.count === 'string' && topic.count.includes('讨论量') 
                    ? topic.count 
                    : `${threadCount}帖子 ${likeCount + postCount + shareCount}讨论量`
            };
        });
    }

    const positionStyle: React.CSSProperties = {
        position: 'absolute',
        ...position
    };

    const handleToggleSelect = (topic: Topic) => {
        const isSelected = selectedTopics.some(t => t.id === topic.id);
        if (!isSelected && selectedTopics.length >= 3) {
            Toast.info('最多添加3个话题');
            return;
        }
        const nextSelection = isSelected
            ? selectedTopics.filter(t => t.id !== topic.id)
            : [...selectedTopics, topic];
        setSelectedTopics(nextSelection);
        onSelectionChange?.(nextSelection);
    };

    const handleConfirm = () => {
        onSelect(selectedTopics);
        if (closeOnSelect) onClose();
    };

    // 话题标签
    const tagRender = (item) => {
        const tags: any = [];

        // 规则 只显示一个标签，优先级为热>新>荐
        if (item.isHot) {
            tags.push({ img: `${BASE_IMG_URL}/common/topic_hot.png`, alt: '热' });
        } else if (item.isNew) {
            tags.push({ img: `${BASE_IMG_URL}/common/topic_new.png`, alt: '新' });
        } else if (item.isRecommended) {
            tags.push({ img: `${BASE_IMG_URL}/common/topic_recommend.png`, alt: '荐' });
        }

        if (tags.length === 0) {
            return null;
        }

        return (
            <>
                {tags.map((tag, index) => (
                    <img
                        key={index}
                        src={tag.img}
                        alt={tag.alt}
                        className={styles.topicTag}
                        title={tag.alt}
                    />
                ))}
            </>
        );
    };

    const getCategorySelectedCount = (category: string) => {
        return selectedTopics.filter(t => {
            // 如果话题有明确的分类信息，直接比较
            if (t.category) {
                return t.category === category;
            }
            
            // 如果话题没有分类信息，通过话题列表推断
            // 在当前分类的话题列表中查找是否有匹配的话题
            const topicInCurrentCategory = displayTopicList.find(topic => 
                String(topic.id) === String(t.id) && 
                (topic.category === category || (!topic.category && category === activeCategory))
            );
            
            return !!topicInCurrentCategory;
        }).length;
    };
    

    return (
        <div className={styles.topicPickerWrap} style={positionStyle} ref={pickerRef}>
            {/* 搜索框 */}
            <div className={styles.searchBar}>
                <div className={styles.searchBox}>
                    <img
                        src={setPostImage.searchSetpost}
                        alt="search"
                        className={styles.searchIcon}
                    />
                    <input
                        type="text"
                        placeholder="搜索话题"
                        className={styles.searchInput}
                        value={searchText}
                        onChange={handleSearchChange}
                    />
                </div>
            </div>

            {/* 内容区域 */}
            <div className={styles.contentArea}>
                {/* 左侧分类 - 搜索时隐藏 */}
                {!searchText && categoryList.length > 0 && (
                    <div className={styles.categoryList}>
                        {categoryList.map(category => {
                            const selectedCount = getCategorySelectedCount(category);
                            return (
                                <div
                                    key={category}
                                    className={`${styles.categoryItem} ${activeCategory === category ? styles.categoryItemActive : ''}`}
                                    role="button"
                                    tabIndex={0}
                                    aria-pressed={activeCategory === category}
                                    onKeyDown={(event) => {
                                        if (event.key === 'Enter' || event.key === ' ') {
                                            event.preventDefault();
                                            event.currentTarget.click();
                                        }
                                    }}
                                    onClick={() => {
                                        setActiveCategory(category);
                                        setPage(1);
                                        setHasMore(true);
                                        // 切换分类时滚动列表回顶部
                                        if (listRef.current) {
                                            listRef.current.scrollTop = 0;
                                        }
                                        if (onCategoryChange) {
                                            onCategoryChange(category, 1);
                                        }
                                    }}
                                >
                                    <span className={styles.categoryName}>{category}</span>
                                    {selectedCount > 0 && (
                                        <span className={styles.categoryBadge}>{selectedCount}</span>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                )}

                {/* 右侧话题列表 */}
                <div ref={listRef} className={`${styles.topicList} ${searchText ? styles.topicListFull : ''}`}>
                    {forceLoading ? (
                        <div className={styles.emptyState}>
                            <div className={styles.emptyText}>加载中...</div>
                        </div>
                    ) : filteredTopics.length === 0 && searchText ? (
                        <div className={styles.emptyState}>
                            <img
                                src={setPostImage.emptyIcon}
                                alt="empty"
                                className={styles.emptyIcon}
                            />
                            <div className={styles.emptyText}>未搜索到相关话题</div>
                        </div>
                    ) : filteredTopics.length === 0 && !searchText ? (
                        <div className={styles.emptyState}>
                            <img
                                src={setPostImage.emptyIcon}
                                alt="empty"
                                className={styles.emptyIcon}
                            />
                            <div className={styles.emptyText}>{loading === false ? '暂无话题' : '数据加载中，请稍候...'}</div>
                        </div>
                    ) : (
                        <>
                            {filteredTopics.map((topic, idx) => {
                                const isSelected = selectedTopics.some(t => t.id === topic.id);
                                return (
                                    <div
                                        key={`${topic.id}-${topic.category || activeCategory || ''}-${idx}`}
                                        className={styles.topicItem}
                                        role="checkbox"
                                        tabIndex={0}
                                        aria-checked={isSelected}
                                        aria-label={topic.name}
                                        onKeyDown={(event) => {
                                            if (event.key === 'Enter' || event.key === ' ') {
                                                event.preventDefault();
                                                handleToggleSelect(topic);
                                            }
                                        }}
                                        onClick={() => handleToggleSelect(topic)}
                                    >
                                        <div className={styles.topicInfo}>
                                            <img src={topic.cover} className={styles.topicCover} />
                                            <div className={styles.topicContent}>
                                                <div className={styles.topicName}>
                                                    <span className={styles.topicText}>{topic.name}</span>
                                                    {tagRender(topic)}
                                                </div>
                                                <div className={styles.topicCount}>{topic.count}</div>
                                            </div>
                                        </div>
                                        <img
                                            src={isSelected
                                                ? setPostImage.atCheckoutActive
                                                : setPostImage.atCheckout
                                            }
                                            alt="check"
                                            className={styles.checkIcon}
                                        />
                                    </div>
                                );
                            })}
                            {/* 列表底部状态：加载更多 / 加载中 / 没有更多 */}
                            {!searchText && (
                                <div className={styles.listFooter}>
                                    {isLoadingMore ? (
                                        <div className={styles.footerText}>加载中...</div>
                                    ) : hasMore ? (
                                        <div className={styles.footerText} onClick={loadMore} style={{ cursor: 'pointer' }}>滚动加载更多</div>
                                    ) : (
                                        <div className={styles.footerText}>没有更多了</div>
                                    )}
                                </div>
                            )}
                        </>
                    )}
                </div>
            </div>

            {/* 底部确认按钮 */}
            <div className={styles.bottomBar}>
                <button
                    className={`${styles.confirmBtn} ${selectedTopics.length > 0 ? styles.confirmBtnActive : ''}`}
                    onClick={handleConfirm}
                    disabled={selectedTopics.length === 0}
                >
                    确定
                </button>
            </div>
        </div>
    );
};

export default TopicPicker;
